import {
  Avatar,
  EmptyState,
  FlexBox,
  InputSelectionListProps,
  SelectionList,
  Spinner,
  Text,
} from '@dixa/lib-shared-habla';
import {
  HookFormFieldWrappedBaseProps,
  HookFormSelectionList,
} from 'lib-shared-hook-form';
import { ReadAgentsApiArg, User } from 'lib-shared-types-api';
import { PropsWithChildren } from 'lib-shared-types-utils';
import { ComponentProps, useCallback, useMemo, useState } from 'react';
import { FieldPath, FieldValues } from 'react-hook-form';
import { useMount, useUpdateEffect } from 'react-use';
import { Waypoint } from 'react-waypoint';

import { usersApi } from 'apis/users';
import { useLazyPaginatedEndpoint } from 'utils/apis/pagination/useLazyPaginatedEndpoint';
import { getUserDisplayName } from 'utils/user';

import styles from './hookFormAgentSelect.module.scss';

type HookFormAgentSelectProps<TFieldValue> = Pick<
  ReadAgentsApiArg,
  'excludeQueueId' | 'excludeTeamId'
> & {
  /**
   * Whether to load the initial list of agents on mount (`lazy: false`) or when
   * the dropdown is opened for the first time (`lazy: true`).
   */
  lazy?: boolean;
} & Pick<InputSelectionListProps<TFieldValue>, 'multiple'>;

/**
 * Represents the React Hook Form variant of the {@link InputSelectionList}
 * component for searching and selecting agents.
 */
export const HookFormAgentSelect = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  children,
  ...props
}: HookFormFieldWrappedBaseProps<
  TFieldValues,
  TName,
  HookFormAgentSelectProps<TFieldValues>
> &
  PropsWithChildren): JSX.Element => {
  const {
    lazy = true,
    excludeQueueId,
    excludeTeamId,
    ...componentProps
  } = props.componentProps ?? {};

  const {
    arg,
    asyncStatus,
    items: users,
    changeArg,
    search,
    hasNextPage,
    nextPage,
  } = useLazyPaginatedEndpoint({
    endpoint: usersApi.endpoints.readAgents,
    itemsSelector: ({ data }) => data.agents,
    totalSelector: ({ data }) => data.number_of_agents,
    infinite: true,
  });

  const initialArg = useMemo<ReadAgentsApiArg>(
    () => ({
      sort: { key: 'display_name' },
      excludeQueueId,
      excludeTeamId,
    }),
    [excludeQueueId, excludeTeamId],
  );

  const [searchTerm, setSearchTerm] = useState('');

  useMount(() => {
    if (!lazy) {
      changeArg(initialArg, { debounceTime: 0 });
    }
  });

  useUpdateEffect(() => {
    search(searchTerm, { debounceTime: 300 });
  }, [search, searchTerm]);

  const handleToggleDropdown = useCallback(
    (newIsOpen: boolean): void => {
      // If the dropdown is closed, reset the search term
      if (!newIsOpen) {
        setSearchTerm('');
      }

      // If the dropdown is opened and lazy loading is enabled, load the initial list
      if (lazy && newIsOpen && !users.length) {
        changeArg(initialArg, { debounceTime: 0 });
      }
    },
    [changeArg, initialArg, lazy, users.length],
  );

  const inputSelectionListProps = useMemo(
    (): ComponentProps<
      typeof HookFormSelectionList<User>
    >['componentProps'] => ({
      placeholder: 'Select an agent',
      equalityFn: (a, b) => a.id === b.id,
      selectionListProps: {
        className: styles.selectionList,
      },
      filter: {
        value: searchTerm,
        onChange: (e) => setSearchTerm(e.target.value),
      },
      getItemTitleByValue: (user) => (user ? getUserDisplayName(user) : ''),
      onToggleDropdown: handleToggleDropdown,
      ...componentProps,
    }),
    [componentProps, handleToggleDropdown, searchTerm],
  );

  return (
    <HookFormSelectionList {...props} componentProps={inputSelectionListProps}>
      {users.map((user) => {
        const name = getUserDisplayName(user);
        const secondLine = user.email ?? user.phone_number;
        const isNameAndSecondLineEqual = name === secondLine;

        return (
          <SelectionList.Item
            key={user.id}
            value={user}
            contentClassName={styles.selectionListItemContent}
          >
            <Avatar
              user={{
                name: user.name,
                avatar_url: user.avatar_url,
                phone_number: user.phone_number,
                email: user.email,
              }}
            />

            <div>
              <Text.Heading variant="heading-4">{name}</Text.Heading>

              {!isNameAndSecondLineEqual && (
                <Text.Paragraph variant="4" subdued>
                  <FlexBox gap="200" alignItems="center">
                    {user.phone_number && <span>{user.phone_number}</span>}
                    {user.email && <span>{user.email}</span>}
                  </FlexBox>
                </Text.Paragraph>
              )}
            </div>
          </SelectionList.Item>
        );
      })}

      {asyncStatus === 'pending' && (
        <FlexBox as="li" padding="300" justifyContent="center">
          <Spinner size={20} />
        </FlexBox>
      )}

      {asyncStatus === 'fulfilled' && !users.length && (
        <FlexBox as="li" padding="300" justifyContent="center">
          <EmptyState
            title={arg?.searchTerm ? 'No agents found' : 'No agents available'}
            description={
              arg?.searchTerm
                ? 'Try changing your search criteria.'
                : 'Your organization has no agents available.'
            }
          />
        </FlexBox>
      )}

      {asyncStatus === 'fulfilled' && hasNextPage && (
        <Waypoint onEnter={nextPage} />
      )}
    </HookFormSelectionList>
  );
};
